import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ButtonBase } from '@mui/material';
import { Inbox, Send, AccessTime } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const StatusToggle = ({ selectedStatus, onStatusChange }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [activeEl, setActiveEl] = useState(null);

  const statusOptions = [
    { value: 'new', label: 'Queries', icon: Inbox, color: 'neutral' },
    { value: 'pitchInbox', label: 'Inbox', icon: Send, color: 'primary' },
    { value: 'pending', label: 'Submitted', icon: AccessTime, color: 'success' },
  ];

  useEffect(() => {
    const activeButton = containerRef.current?.querySelector(`[data-status="${selectedStatus}"]`);
    setActiveEl(activeButton);
  }, [selectedStatus]);

  return (
    <Box ref={containerRef} sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      {statusOptions.map(({ value, label, icon: Icon, color }, index) => (
        <ButtonBase
          key={value}
          data-status={value}
          onClick={() => onStatusChange(value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1.5,
            minWidth: 120,
            position: 'relative',
            backgroundColor:
              selectedStatus === value
                ? theme.palette[color].main
                : color === 'neutral'
                ? '#e0e0e0'
                : color === 'warning'
                ? '#fff3e0'
                : color === 'primary'
                ? '#bbdefb'
                : '#c8e6c9',
            color: selectedStatus === value ? (color === 'neutral' ? '#fff' : theme.palette[color].contrastText) : '#424242',
            border: 'none',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to right, ${
                selectedStatus === value
                  ? theme.palette[color].main
                  : color === 'neutral'
                  ? '#bdbdbd'
                  : color === 'warning'
                  ? '#ffcc80'
                  : color === 'primary'
                  ? '#90caf9'
                  : '#a5d6a7'
              }, ${
                selectedStatus === value
                  ? theme.palette[color].main
                  : color === 'neutral'
                  ? '#bdbdbd'
                  : color === 'warning'
                  ? '#ffcc80'
                  : color === 'primary'
                  ? '#90caf9'
                  : '#a5d6a7'
              })`,
              clipPath:
                index === 0 && index === statusOptions.length - 1
                  ? 'none'
                  : index === 0
                  ? 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%)'
                  : index === statusOptions.length - 1
                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 15px 50%)'
                  : 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%, 15px 50%)',
              zIndex: -1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '1px',
              left: '1px',
              right: '1px',
              bottom: '1px',
              backgroundColor:
                selectedStatus === value
                  ? theme.palette[color].main
                  : color === 'neutral'
                  ? '#e0e0e0'
                  : color === 'warning'
                  ? '#fff3e0'
                  : color === 'primary'
                  ? '#bbdefb'
                  : '#c8e6c9',
              clipPath:
                index === 0 && index === statusOptions.length - 1
                  ? 'none'
                  : index === 0
                  ? 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%)'
                  : index === statusOptions.length - 1
                  ? 'polygon(1px 1px, calc(100% - 1px) 1px, calc(100% - 1px) calc(100% - 1px), 1px calc(100% - 1px), 16px 50%)'
                  : 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%, 15px 50%)',
              zIndex: -1,
            },
            textTransform: 'none',
            fontWeight: selectedStatus === value && index !== statusOptions.length - 1 ? 600 : 500,
            transition: 'all 0.2s ease-in-out',
            boxShadow:
              selectedStatus === value
                ? color === 'neutral'
                  ? '0 2px 8px rgba(97, 97, 97, 0.3), 0 0 0 2px rgba(97, 97, 97, 0.2)'
                  : `0 2px 8px ${theme.palette[color].main}33, 0 0 0 2px ${theme.palette[color].main}20`
                : '0 1px 3px rgba(0,0,0,0.1)',
            clipPath:
              index === 0 && index === statusOptions.length - 1
                ? 'none' // Single item - no clipping
                : index === 0
                ? 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%)' // First: straight left, arrow right
                : index === statusOptions.length - 1
                ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 15px 50%)' // Last: cut-out left, straight right
                : 'polygon(0 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 0 100%, 15px 50%)', // Middle: cut-out left, arrow right
            borderRadius: statusOptions.length === 1 ? 1.5 : index === 0 ? '6px 0 0 6px' : index === statusOptions.length - 1 ? '0 6px 6px 0' : 0,
            '&:hover':
              selectedStatus !== value
                ? {
                    backgroundColor: color === 'neutral' ? '#bdbdbd' : color === 'warning' ? '#ffcc80' : color === 'primary' ? '#90caf9' : '#a5d6a7',
                    color: color === 'neutral' ? '#424242' : color === 'warning' ? '#e65100' : color === 'primary' ? '#0d47a1' : '#1b5e20',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    '&::after': {
                      backgroundColor:
                        color === 'neutral' ? '#bdbdbd' : color === 'warning' ? '#ffcc80' : color === 'primary' ? '#90caf9' : '#a5d6a7',
                    },
                  }
                : {},
          }}
        >
          <Icon fontSize="small" />
          <Typography variant="body2" component="span" sx={{ fontWeight: 'inherit', whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        </ButtonBase>
      ))}
      {activeEl && (
        <motion.div
          initial={false}
          animate={{
            x: activeEl.offsetLeft,
            width:
              statusOptions.findIndex((opt) => opt.value === selectedStatus) === statusOptions.length - 1
                ? activeEl.offsetWidth
                : activeEl.offsetWidth - 15,
          }}
          transition={{ ease: 'easeOut', duration: 0.3 }}
          style={{
            height: '3px',
            backgroundColor:
              selectedStatus === 'new'
                ? '#424242'
                : selectedStatus === 'draft'
                ? theme.palette.warning.dark
                : selectedStatus === 'pitchInbox'
                ? theme.palette.primary.dark
                : theme.palette.success.dark,
            position: 'absolute',
            bottom: '-8px',
            borderRadius: '2px',
          }}
        />
      )}
    </Box>
  );
};

StatusToggle.propTypes = {
  selectedStatus: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default StatusToggle;
